import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from 'bootstrap';

import config from '../config';
const { EMAIL, HOOK, PUSH, SMS } = config.notification.type.values;

import { useLazyQuery, useMutation } from '../hooks/graph';
import { formControlInvalid } from '../components/forms';
const controller = new AbortController();

function MeConnectorAccountNotifications({ connector, account }) {
	const [ getNotifications, { data, fetching } ] = useLazyQuery('notifications', controller);
	const [ notificationCreate ] = useMutation('notificationCreate', controller);
	const [ notificationUpdate ] = useMutation('notificationUpdate', controller);
	const [ notificationDelete ] = useMutation('notificationDelete', controller);
	const ref = useRef(null);

	const { register, reset, watch, handleSubmit,
		setError, clearErrors, getValues, formState: { errors }
	} = useForm();

	// weak?
	const watchIsEnabledEmail = watch('notifications.0.isEnabled');
	const watchIsEnabledHook = watch('notifications.1.isEnabled');
	const watchIsEnabledPush = watch('notifications.2.isEnabled');
	const watchIsEnabledSms = watch('notifications.3.isEnabled');

	const onSubmit = async values => {
		// update notifications
		for (const notification of values.notifications) {
			const dataNotification = data.notifications.find(
				item => item.type == notification.type);

			if (!notification.isEnabled) {
				dataNotification && await notificationDelete(
					{id: dataNotification.id}).catch(); // display?
				continue;
			}

			if (dataNotification) {
				await notificationUpdate({...dataNotification,
					metadata: JSON.stringify(notification.metadata)}).catch(); // display?
				continue;
			}

			await notificationCreate({
				...notification,
				metadata: JSON.stringify(notification.metadata),
				connectorId: connector.id,
				accountId: account.id,
			}).catch(err => {
				setError('submit', {message: err.message || 'Error'});
				throw err;
			});
		}

		// refresh notification list: to remove/add ids
		getNotifications({connectorId: connector.id, accountId: account.id});

		// close the modal (if there is no form errors)
		Modal.getOrCreateInstance(ref.current).hide();
	};

	useEffect(() => {
		data && reset({
			notifications: getValues().notifications.map(notification => {
				const dataNotification = data.notifications.find(
					item => item.type == notification.type);

				return {
					type: notification.type,
					isEnabled: !!dataNotification,
					...dataNotification,
				};
			}),
		});
	}, [data]);

	useEffect(() => {
		connector && account && getNotifications({
			connectorId: connector.id, accountId: account.id,
		});
	}, [connector, account]);

	useEffect(() => () => controller.abort(), []); // abort on unmount

	return (
		<div ref={ref} className="modal" id="connectorAccountNotificationsModal" tabIndex="-1" aria-labelledby="connectorAccountNotificationsModalLabel">
			<div className="modal-dialog">
				{
					connector && account && data && !fetching && (
						<div className="modal-content">

							<div className="modal-header">
								<h5 className="modal-title" id="connectorAccountNotificationsModalLabel">Notifications</h5>
								<button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
							</div>

							<div className="modal-body">
								<form className="d-grid"
									onFocus={() => errors.submit && clearErrors('submit')}
								>
									<div className="form-check">
										<input {...register('notifications.0.isEnabled')} type="checkbox" value="" id="notifications.0.isEnabled" className="form-check-input" />
										<label className="form-check-label" htmlFor="notifications.0.isEnabled">
											Send email
										</label>

										<div className="text-secondary opacity-75 mt-1">Provides email notification to the specified address.</div>
										<input {...register('notifications.0.type', {valueAsNumber: true})} type="hidden" value={EMAIL} />

										{
											watchIsEnabledEmail && (
												<div>
													<div className="mt-2">
														<input {...register('notifications.0.metadata.email', {required: 'Required', pattern: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}/i})} type="email" placeholder="Email address" id="notifications.0.metadata.email" className={formControlInvalid(errors.notifications?.[0]?.metadata?.email, 'form-control')} />
														{errors.notifications?.[0]?.metadata?.email?.type === 'required' && <span className="invalid-feedback">{errors.notifications[0].metadata.email.message}</span>}
														{errors.notifications?.[0]?.metadata?.email?.type === 'pattern' && <span className="invalid-feedback">Invalid email</span>}
													</div>
												</div>
											)
										}
									</div>

									<div className="form-check mt-3">
										<input {...register('notifications.1.isEnabled')} type="checkbox" value="" id="notifications.1.isEnabled" className="form-check-input" />
										<label className="form-check-label" htmlFor="notifications.1.isEnabled">
											Call webhook
										</label>

										<div className="text-secondary opacity-75 mt-1">Sends a POST request to the specified URL.</div>
										<input {...register('notifications.1.type', {valueAsNumber: true})} type="hidden" value={HOOK} />

										{
											watchIsEnabledHook && (
												<div>
													<div className="mt-2">
														<input {...register('notifications.1.metadata.url', {required: 'Required', pattern: /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/i}) /* eslint-disable-line no-useless-escape */} type="url" placeholder="URL address" id="notifications.1.metadata.url" className={formControlInvalid(errors.notifications?.[1]?.metadata?.url, 'form-control')} />
														{errors.notifications?.[1]?.metadata?.url?.type === 'required' && <span className="invalid-feedback">{errors.notifications[1].metadata.url.message}</span>}
														{errors.notifications?.[1]?.metadata?.url?.type === 'pattern' && <span className="invalid-feedback">Invalid URL</span>}
													</div>
												</div>
											)
										}
									</div>

									<div className="form-check mt-3 text-muted">
										<input {...register('notifications.2.isEnabled', {disabled: true})} type="checkbox" value="" id="notifications.2.isEnabled" className="form-check-input" />
										<label className="form-check-label" htmlFor="notifications.2.isEnabled">
											Send push messages
										</label>

										<input {...register('notifications.2.type', {valueAsNumber: true})} type="hidden" value={PUSH} />

										{
											watchIsEnabledPush && (
												<div>
												</div>
											)
										}
									</div>

									<div className="form-check mt-3 text-muted">
										<input {...register('notifications.3.isEnabled', {disabled: true})} type="checkbox" value="" id="notifications.3.isEnabled" className="form-check-input" />
										<label className="form-check-label" htmlFor="notifications.3.isEnabled">
											Send SMS
										</label>

										<input {...register('notifications.3.type', {valueAsNumber: true})} type="hidden" value={SMS} />

										{
											watchIsEnabledSms && (
												<div>
												</div>
											)
										}
									</div>

									{
										// reserved
										// data && error && errors.submit && (
										// 	<div className="alert alert-danger mt-3" role="alert">
										// 		{errors.submit.message}
										// 	</div>
										// )
									}
								</form>
							</div>

							<div className="modal-footer">
								<button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
								<button type="button" className="btn btn-primary" onClick={handleSubmit(onSubmit)}>Save</button>
							</div>
						</div>
					)
				}
			</div>
		</div>
	);
}

export default MeConnectorAccountNotifications;
